import { Filter, Maximize, Minimize, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Button,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    Separator,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import { PanelCloseButton } from '@/components/shared/SlidingPanel';
import type { EntityMapFilterState } from '@/components/map/utils/entityMapFilters';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { TableViewLayout } from '@/table-view/TableViewContext';

import { NetworkTablePanel, type NetworkTableActions } from './NetworkTablePanel';
import { RecordCountHeader } from './RecordCountHeader';
import { DEFAULT_TABLE_SCROLL_CHROME, type TableHeaderChrome } from './useTableScrollChrome';

/** Matches the map search's debounce (useMapSearch.ts). */
const SEARCH_DEBOUNCE_MS = 350;

interface NetworkTableRegionProps {
    /** Effective layout (already clamped for mobile by the caller). */
    layout: TableViewLayout;
    /** Active client whose network the table displays. */
    clientId: string | null;
    /**
     * The map's committed filters, or null when filters are inactive — the same state that
     * drives the map, so both views show one POI set. The table owns no filter state.
     */
    filters: EntityMapFilterState | null;
    /** Map-facing row actions. Locate navigates + highlights only; Show Details opens the panel. */
    actions: NetworkTableActions;
    /** Grow the table to fill the workspace, hiding the map. */
    onMaximise: () => void;
    /** Restore the 50/50 split from the maximised state. */
    onRestoreSplit: () => void;
    /** Close the table, returning the map to full width. */
    onClose: () => void;
    /**
     * Toggles the map's Filters panel — the SAME committed filters feed this table's rows (see
     * `filters` above), so the panel is how the table's POI set is narrowed too. The panel renders
     * beside the whole workspace, so it stays reachable even when the table is maximised and the
     * map (with its own Filters control) is hidden.
     */
    onToggleFilters: () => void;
}

/**
 * The Network Overview table region — the third flex child of the map-and-table
 * workspace. It owns only its own width for the three layout states; its data and
 * rows are filled in by later stages. Kept mounted (at zero width, inert) when
 * closed so its UI state and scroll position survive open/close.
 */
export function NetworkTableRegion({
    layout,
    clientId,
    filters,
    actions,
    onMaximise,
    onRestoreSplit,
    onClose,
    onToggleFilters,
}: NetworkTableRegionProps) {
    const isClosed = layout === 'closed';
    const isMaximised = layout === 'maximised';

    // 768px == MapView's own isDesktop breakpoint (and shared-ui's MOBILE_BREAKPOINT). Held once
    // here — the single media subscription for the region — and threaded down as props, so the
    // header and the panel can never disagree about which presentation is active.
    const isDesktop = useMediaQuery('(min-width: 768px)');

    // The raw input is table UI state; `committedSearch` is what queries use. Debounced in the
    // change handler (never an effect) — the map search's pattern.
    const [searchText, setSearchText] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        return () => {
            clearTimeout(debounceRef.current);
        };
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setSearchText(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCommittedSearch(value);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    // Clearing is immediate — waiting 350ms to undo a search reads as lag.
    const handleSearchClear = useCallback(() => {
        clearTimeout(debounceRef.current);
        setSearchText('');
        setCommittedSearch('');
    }, []);

    return (
        <aside
            aria-label="Network Overview table"
            aria-hidden={isClosed}
            inert={isClosed}
            className={cn(
                'bg-background flex h-full flex-col overflow-hidden',
                isClosed && 'w-0',
                layout === 'split' && 'w-1/2 shrink-0 border-l',
                // No border-l when maximised: nothing visible sits to the left (the map column is
                // hidden on desktop, squeezed to zero width on mobile), so the region's left edge
                // meets the app sidebar, which already draws its own border-r — a border here
                // doubles it. In split the border separates the table from the map.
                //
                // Desktop maximised is flex-1 (fills whatever the row has, no width to animate);
                // mobile maximised is an animatable w-full — shrink-0 makes it non-negotiable, so
                // the flex-1 map column beside it is squeezed to zero instead of sharing the row.
                isMaximised && (isDesktop ? 'min-w-0 flex-1' : 'w-full min-w-0 shrink-0'),
                // Width animates between closed and split everywhere, and to/from maximised on
                // mobile — the same slide the side panels have, instead of the table just
                // appearing. Desktop maximised has no width to transition (flex-1), so the class
                // would be inert there anyway.
                (!isMaximised || !isDesktop) && 'transition-[width] duration-200 ease-in-out',
            )}
        >
            {/*
             * Remount on client switch so all table state (pages, expansion, cached children) resets.
             * Keyed on the client ONLY: a filter/search/sort change must reset the data (which the
             * hook's query context handles) while keeping this scroll container and the layout intact.
             */}
            <NetworkTablePanel
                key={clientId ?? 'no-client'}
                clientId={clientId}
                enabled={!isClosed}
                filters={filters}
                search={committedSearch}
                isMaximised={isMaximised}
                isMobile={!isDesktop}
                actions={actions}
                // The header renders inside the panel because it displays the panel's record total.
                // Search text stays owned here, so it survives the panel's remount on client switch.
                // The chrome argument defaults to at-rest so callers (and the test mock) that only
                // pass the total keep working.
                renderHeader={(totalFilteredPoiCount, scrollChrome = DEFAULT_TABLE_SCROLL_CHROME) => (
                    <>
                        <NetworkTableHeader
                            isMaximised={isMaximised}
                            isDesktop={isDesktop}
                            scrollChrome={scrollChrome}
                            total={totalFilteredPoiCount}
                            committedSearch={committedSearch}
                            searchText={searchText}
                            onSearchChange={handleSearchChange}
                            onSearchClear={handleSearchClear}
                            onMaximise={onMaximise}
                            onRestoreSplit={onRestoreSplit}
                            onClose={onClose}
                            onToggleFilters={onToggleFilters}
                            // Null already means "no filters committed" — the same contract the
                            // panel's data fetching relies on.
                            hasActiveFilters={filters !== null}
                        />
                        {/* Desktop's header underline only. The mobile header draws its own
                            border+shadow boundaries (title block and search block), and this
                            Separator landed as a stray extra 1px line directly above the column
                            headings — the design (docs/Search.png) has no line there. */}
                        {isDesktop && <Separator />}
                    </>
                )}
            />
        </aside>
    );
}

interface NetworkTableHeaderProps {
    isMaximised: boolean;
    /** The region's single breakpoint reading; below it the mobile presentation renders. */
    isDesktop: boolean;
    /** The scroll container's chrome state, driving the mobile shadow and search retraction. */
    scrollChrome: TableHeaderChrome;
    /** Server total for the current filter/search context, shown beneath the title. */
    total: number;
    /** The committed term, which the record-count wording reflects. */
    committedSearch: string;
    searchText: string;
    onSearchChange: (value: string) => void;
    onSearchClear: () => void;
    onMaximise: () => void;
    onRestoreSplit: () => void;
    onClose: () => void;
    onToggleFilters: () => void;
    /** True when committed filters are narrowing the rows — surfaces the badge on the header. */
    hasActiveFilters: boolean;
}

function NetworkTableHeader({
    isMaximised,
    isDesktop,
    scrollChrome,
    total,
    committedSearch,
    searchText,
    onSearchChange,
    onSearchClear,
    onMaximise,
    onRestoreSplit,
    onClose,
    onToggleFilters,
    hasActiveFilters,
}: NetworkTableHeaderProps) {
    const toggleLabel = isMaximised ? 'Minimise table' : 'Maximise table';
    // Pins the retractable search block while focus is anywhere inside it — retracting the block
    // out from under the keyboard mid-search would be hostile. focus/blur bubble in React
    // (focusin/focusout), so the wrapper hears its descendants.
    const [searchBlockFocused, setSearchBlockFocused] = useState(false);
    // JS-gated, not a `motion-reduce:` variant: that variant and its base transition utility are
    // the same-property pair this bundle's emission order breaks (see the comments below).
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    if (!isDesktop) {
        // Visible at the top, retracted while scrolling down, back on any upward scroll, and
        // pinned while in use. Deliberately derived from `direction` while the header's shadow
        // derives from `atTop` — the two react to different things and must not share a boolean
        // (the shadow stays while the block is away, and vice versa).
        const searchBlockVisible = scrollChrome.atTop || scrollChrome.direction !== 'down' || searchBlockFocused;

        return (
            <>
                {/*
                 * The permanent header: title, record count and the close button — the one control
                 * that must survive every scroll state. No left indent for the sidebar hamburger:
                 * that trigger is hidden while the table is open on mobile (see FindAndFixContent),
                 * which is exactly what frees this row to start at the panel edge.
                 *
                 * The shadow marks "content has scrolled beneath" and follows `atTop` alone. It
                 * needs `relative` with a raised z-index so it paints over the search block below
                 * rather than being buried by it.
                 */}
                <div
                    className={cn(
                        // py-4.5 (18px): with the lg/leading-7 title and the records line this
                        // lands the block at the design's ~87px header, in step with the other
                        // side panels' breathing room (their single-line headers centre in h-20).
                        // border-b + the cast shadow below close the block, per docs/Search.png —
                        // the same boundary treatment the search block gets.
                        'relative z-[1] flex items-start justify-between gap-3 border-b px-6 py-4.5',
                        !prefersReducedMotion && 'transition-shadow duration-200',
                        !scrollChrome.atTop &&
                            '[box-shadow:0_4px_12px_-4px_rgba(0,0,0,0.08)] dark:[box-shadow:0_4px_12px_-4px_rgba(0,0,0,0.5)]',
                    )}
                >
                    <div className="flex min-w-0 flex-col gap-1">
                        {/* One step down from the desktop header's text-2xl: Poppins Black
                            lg/leading-7, per the mobile design. */}
                        <h2 className="font-poppins text-lg leading-7 font-black tracking-normal uppercase">
                            Network Overview
                        </h2>
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <RecordCountHeader total={total} search={committedSearch} onClearSearch={onSearchClear} />
                            {/* Stays on the permanent row, not in the retractable block: narrowed
                                counts need their explanation visible even while scrolled. */}
                            {hasActiveFilters && <FiltersAppliedBadge />}
                        </div>
                    </div>
                    <div className="shrink-0">
                        <PanelCloseButton
                            onClick={onClose}
                            aria-label="Close Network Overview table"
                            iconStrokeWidth={1.5}
                            absoluteIconStroke
                        />
                    </div>
                </div>
                {/* Hangs off the header (not the retractable block below), so this boundary stays
                    drawn while the search is scrolled away. */}
                <HeaderCastShadow />
                {/*
                 * The retractable search + filters block. Collapsed via grid-template-rows rather
                 * than unmounting, so the input keeps its DOM, value and any composition state
                 * while hidden; `inert` + `aria-hidden` take it out of the tab order and the
                 * accessibility tree meanwhile. The Filters sheet needs no extra pinning: it can
                 * only be opened from this block while visible, and on mobile it is a modal sheet
                 * that blocks scrolling the table behind it.
                 */}
                {isMaximised && (
                    <div
                        className={cn(
                            'grid',
                            searchBlockVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                            !prefersReducedMotion && 'transition-[grid-template-rows] duration-200 ease-in-out',
                        )}
                        inert={!searchBlockVisible}
                        aria-hidden={!searchBlockVisible}
                    >
                        <div className="min-h-0 overflow-hidden">
                            {/* Per the design (docs/Search.png): the search row closes with a 1px
                                border AND the cast shadow falling from it onto the headings zone —
                                the title block above gets the plain border alone. Both live inside
                                the collapsible, so the boundary retracts with the search. */}
                            {/* 20px of air above and below the box: the title's 8px shadow strip
                                is in-flow above this row, so pt-3 (12px) completes the upper 20;
                                pb-5 is the lower 20 down to this row's own border. */}
                            <div
                                className="flex items-center gap-2.5 border-b px-6 pt-3 pb-5"
                                onFocus={() => {
                                    setSearchBlockFocused(true);
                                }}
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        setSearchBlockFocused(false);
                                    }
                                }}
                            >
                                {/* Full width here: the block is the search's own row, so the
                                    desktop cap that shares space with the title does not apply. */}
                                <NetworkTableSearch
                                    value={searchText}
                                    onChange={onSearchChange}
                                    onClear={onSearchClear}
                                    className="max-w-none"
                                />
                                <FiltersButton onClick={onToggleFilters} />
                            </div>
                            <HeaderCastShadow />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        /*
         * The desktop header row (the mobile presentation returned above).
         *
         * min-h-23 = 92px (23 × 0.25rem) — a MINIMUM, not a fixed height, so nothing is clipped if
         * content ever outgrows it (the region hides overflow, so anything that cannot fit is
         * lost, not scrollable).
         *
         * The row never wraps: the controls must stay beside the title, vertically centred, and
         * the parts shrink instead — with the expanded sidebar taking 16rem this header can be
         * ~448px holding ~632px of content, and wrapping would push the controls under the title.
         */
        <div className="flex min-h-23 shrink-0 flex-nowrap items-center justify-between gap-x-4 gap-y-2 px-6 py-3">
            {/*
             * `shrink-0` keeps the title block on ONE line, so the header holds its 92px. The
             * controls absorb the shortfall instead, because a wrapped title costs vertical space
             * while a narrower search costs only width.
             */}
            <div className="flex min-w-0 shrink-0 flex-col gap-1">
                <h2 className="font-poppins text-2xl leading-8 font-black tracking-normal uppercase">
                    Network Overview
                </h2>
                {/*
                 * This carries the ONLY search control that renders in split, where the input above
                 * is absent (see RecordCountHeader). Same clear handler as the input's ×, so both
                 * routes commit immediately rather than through the debounce.
                 */}
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <RecordCountHeader total={total} search={committedSearch} onClearSearch={onSearchClear} />
                    {/*
                     * Rides the record-count line because that is what it qualifies: with filters
                     * committed, every count in the table describes the filtered subset. Shown in
                     * BOTH layouts — in split the Filters button above is absent, so without this
                     * the table would show narrowed numbers with no sign of why.
                     */}
                    {hasActiveFilters && <FiltersAppliedBadge />}
                </div>
            </div>
            {/*
             * `ml-auto` keeps the controls hard right of the title. `min-w-0` lets the search
             * shrink as a last resort; the buttons stay `shrink-0` because they are the only way
             * to close the table.
             */}
            <div className="ml-auto flex min-w-0 items-center justify-end gap-2.5">
                {/* Search is a maximised-only control, per the designs. */}
                {isMaximised && (
                    <NetworkTableSearch value={searchText} onChange={onSearchChange} onClear={onSearchClear} />
                )}
                {/*
                 * Opens the Filters panel — the committed filters drive the table's rows exactly as
                 * they drive the map, so this is where the table's POI set is narrowed. Maximised
                 * only, per the design: there the map (and its own Filters control) is hidden, so
                 * this is the only route to the panel; in split the map control is on screen.
                 */}
                {isMaximised && <FiltersButton onClick={onToggleFilters} />}
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Button
                                variant="ghost"
                                size="icon"
                                // `panel-close-btn` is the shared panel icon-button style (border
                                // + hover fill, see --panel-icon-btn-border) despite its name — it
                                // already dresses the Download/POI panel buttons. Reused rather
                                // than duplicated so this sits identically to the close button.
                                className="panel-close-btn size-10 shrink-0"
                                onClick={isMaximised ? onRestoreSplit : onMaximise}
                                aria-label={toggleLabel}
                            >
                                {/* size PROP as well as class: absoluteStrokeWidth divides by the
                                    prop, so without it the CSS downscale thins the stroke to 1px
                                    instead of the design's true 1.5px. */}
                                {isMaximised ? (
                                    <Minimize
                                        className="text-primary size-4"
                                        size={16}
                                        strokeWidth={1.5}
                                        absoluteStrokeWidth
                                    />
                                ) : (
                                    <Maximize
                                        className="text-primary size-4"
                                        size={16}
                                        strokeWidth={1.5}
                                        absoluteStrokeWidth
                                    />
                                )}
                            </Button>
                        }
                    />
                    {/* "Minimise", not "Restore split": the control is named for its shape on
                        screen, not for the layout state it returns to. */}
                    <TooltipContent side="top">{isMaximised ? 'Minimise' : 'Maximise'}</TooltipContent>
                </Tooltip>
                <div className="shrink-0">
                    <PanelCloseButton
                        onClick={onClose}
                        aria-label="Close Network Overview table"
                        // 1.5px to match the rest of this header's control icons; the map panels
                        // keep the shared default.
                        iconStrokeWidth={1.5}
                        absoluteIconStroke
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * The soft cast shadow under both mobile header boundaries, per the design (docs/Search.png):
 * an 8px strip darkest at the block's 1px border and fading downward. The title block and the
 * search block each pair their border-b with one of these — and nothing else draws a line before
 * the column headings (the desktop header's Separator is deliberately absent on mobile; it was
 * the stray rule that kept reading as a top border on the headings). Dark twin follows the
 * header scroll shadow's idiom of deepening the same black.
 */
function HeaderCastShadow() {
    return (
        <div
            aria-hidden="true"
            className="h-2 shrink-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0)_100%)] dark:bg-[linear-gradient(180deg,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0)_100%)]"
        />
    );
}

/**
 * The Filters panel toggle, shared by the desktop control row and the mobile search block so the
 * two can never drift apart. TooltipTrigger overwrites the Button's data-slot, so the icon must
 * declare its own brand colour (the shared ghost-button rule cannot reach it).
 */
function FiltersButton({ onClick }: { onClick: () => void }) {
    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon"
                        className="panel-close-btn size-10 shrink-0"
                        onClick={onClick}
                        aria-label="Filters"
                    >
                        <Filter className="text-primary size-4" size={16} strokeWidth={1.5} absoluteStrokeWidth />
                    </Button>
                }
            />
            <TooltipContent side="top">Filters</TooltipContent>
        </Tooltip>
    );
}

/**
 * The passive "filters are narrowing these rows" signal. Styled like the search-term chip in
 * RecordCountHeader — the same "this is qualifying your numbers" idiom. Deliberately NOT a
 * button: committing or clearing filters belongs to the Filters panel (whose Reset already
 * handles it), and in split the map's own Filters control is on screen to open it.
 */
function FiltersAppliedBadge() {
    return (
        <span className="bg-muted text-foreground font-poppins inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-sm leading-5">
            <Filter className="text-origin-blue size-3" size={12} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            Filters applied
        </span>
    );
}

function NetworkTableSearch({
    value,
    onChange,
    onClear,
    className,
}: {
    value: string;
    onChange: (value: string) => void;
    onClear: () => void;
    /** Merged last, so a caller can lift the width cap (the mobile block gives it its own row). */
    className?: string;
}) {
    return (
        // h-10 matches the size-10 Maximise/Close buttons beside it; InputGroup defaults to h-8.
        // `w-full max-w-64` rather than a fixed `w-64`: it is 256px wherever there is room (every
        // desktop width), and shrinks instead of pushing the buttons off a narrow screen.
        <InputGroup className={cn('h-10 w-full max-w-64 min-w-0', className)}>
            <InputGroupAddon>
                {/* Explicit colour: InputGroupAddon sets `text-muted-foreground` on itself, which the
                    icon would otherwise inherit. Declared here, it wins. */}
                <Search className="text-origin-blue size-4" size={16} strokeWidth={1.5} absoluteStrokeWidth />
            </InputGroupAddon>
            <InputGroupInput
                type="search"
                value={value}
                placeholder="Search Network"
                aria-label="Search network records"
                className={cn(
                    'placeholder:font-poppins placeholder:text-table-search-placeholder placeholder:text-sm placeholder:leading-5 placeholder:font-normal placeholder:tracking-normal placeholder:italic',
                    // `type="search"` makes the browser draw its own clear button, which appeared as
                    // a second, black × beside the styled one. Suppressing the native control keeps
                    // the one below as the only way to clear — and the only thing that resets the
                    // committed term rather than just emptying the box.
                    '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:[-webkit-appearance:none]',
                )}
                onChange={(event) => {
                    onChange(event.target.value);
                }}
            />
            {value !== '' && (
                <InputGroupAddon align="inline-end">
                    <Button variant="ghost" size="icon" className="size-6" aria-label="Clear search" onClick={onClear}>
                        <X className="size-3.5" size={14} strokeWidth={1.5} absoluteStrokeWidth />
                    </Button>
                </InputGroupAddon>
            )}
        </InputGroup>
    );
}
